import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useInterview } from '@/hooks/useInterview';
import { useCandidate } from '@/hooks/useCandidate';
import { useAuth } from '@/contexts/AuthContext';
import { ScoreReport } from '@/components/interviews/ScoreReport';
import { ReviewChat } from '@/components/interviews/ReviewChat';
import { AppLayout } from '@/components/AppLayout';

export function InterviewResultPage() {
  const { jobId, candidateId, interviewId } = useParams<{
    jobId: string; candidateId: string; interviewId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { interview, isLoading, refetch } = useInterview(jobId!, candidateId!, interviewId!);
  const { candidate } = useCandidate(jobId!, candidateId!);

  if (isLoading) {
    return <AppLayout><div className="p-8 text-center text-muted-foreground">Carregando...</div></AppLayout>;
  }

  if (!interview) {
    return <AppLayout><div className="p-8 text-center text-red-600">Entrevista não encontrada</div></AppLayout>;
  }

  return (
    <AppLayout
      actions={<Button variant="outline" onClick={() => navigate(-1)}>Voltar</Button>}
    >
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Resultado da Entrevista</h1>
        <p className="text-sm text-muted-foreground">
          {candidate?.name} • {interview.mode === 'text' ? '📝 Texto' : '🎙️ Áudio'} •{' '}
          {new Date(interview.conductedAt).toLocaleDateString('pt-BR')}
        </p>
      </div>

      {interview.interviewFeedback && (
        <ScoreReport
          feedback={interview.interviewFeedback}
          finalScore={interview.finalScore}
          fitScore={candidate?.fitScore || 0}
          candidateName={candidate?.name}
          candidateEmail={candidate?.email}
          interviewDate={interview.conductedAt}
          revisedFeedback={interview.revisedFeedback}
          reviewSessions={interview.reviewSessions}
          reviewCount={interview.reviewCount}
        />
      )}

      {/* Review chat */}
      {interview.status === 'done' && (
        <ReviewChat
          jobId={jobId!}
          candidateId={candidateId!}
          interviewId={interviewId!}
          interview={interview}
          userRole={user?.role || 'viewer'}
          onReviewComplete={refetch}
        />
      )}

      {/* Transcrição original */}
      {interview.rawInput && (
        <Card className="p-4">
          <h4 className="font-semibold text-sm mb-2">Transcrição / Resumo Original</h4>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{interview.rawInput}</p>
        </Card>
      )}

      {/* Notas */}
      {interview.notes && (
        <Card className="p-4">
          <h4 className="font-semibold text-sm mb-2">Notas do Recrutador</h4>
          <p className="text-sm text-muted-foreground">{interview.notes}</p>
        </Card>
      )}
    </div>
    </AppLayout>
  );
}
